<template>
  <div class="p-lg pb-leftSquiggleHeight">
    <h1>Tabs</h1>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Tab component</h2>
      <TabItem
        title="Unselected Tab"
        id="tab"
        :selected="false"
      />
      <TabItem
        title="Selected Tab"
        id="tab"
        :selected="true"
      />
      <TabItem
        title="Unselected, Dismissible Tab"
        id="tab"
        :selected="false"
        :dismissible="true"
      />
      <TabItem
        title="Selected, Dismissible Tab"
        id="tab"
        :selected="true"
        :dismissible="true"
      />
      <TabItem
        title="Error Tab"
        id="tab"
        :selected="true"
        :hasError="true"
      />
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Tab List component</h2>
      <TabList
        :tabs="tabListTabs"
        v-model="selectedListTab"
      />
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>
        Tab List in a restricted container to demo scroll controls for
        mobile/limited space
      </h2>
      <div class="max-w-dateRangeComponentMaxW">
        <TabList
          :tabs="stupidAmountOfTabs"
          v-model="selectedStupidTab"
        />
      </div>
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Slot Tabs component</h2>
      <TabList
        :tabs="tabGroupTabs"
        v-model="selectedGroupTab"
      />
      <TabGroup
        :tabs="tabGroupTabs"
        :selected="selectedGroupTab"
      >
        <template v-slot:navy>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-navy text-signableBrandColours-white flex items-center">
            <span>I'm a navy tab</span>
          </div>
        </template>
        <template v-slot:pink>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-pink text-signableBrandColours-white flex items-center">
            <span>I'm a pink tab</span>
          </div>
        </template>
        <template v-slot:orange>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-orange text-signableBrandColours-white flex items-center">
            <span>I'm an orange tab. My hobbies include calligraphy and archery.</span>
          </div>
        </template>
        <template v-slot:red>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-red text-signableBrandColours-white flex items-center">
            <span>I'm a red tab</span>
          </div>
        </template>
      </TabGroup>
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Dismissing Slot Tabs Demo</h2>
      <TabList
        :tabs="tabGroupDismissDemoRef"
        v-model="selectedDismissDemoTab"
        @dismiss="removeDismissDemoTab($event)"
      />
      <TabGroup
        :tabs="tabGroupDismissDemoRef"
        :selected="selectedDismissDemoTab"
      >
        <template v-slot:1>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-signableBlue text-signableBrandColours-white flex items-center">
            <span>You can't dismiss me</span>
          </div>
        </template>
        <template v-slot:2>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-signableBlue text-signableBrandColours-white flex items-center">
            <span>Dismiss me</span>
          </div>
        </template>
        <template v-slot:3>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-signableBlue text-signableBrandColours-white flex items-center">
            <span>Dismiss me</span>
          </div>
        </template>
        <template v-slot:4>
          <div class="w-full h-menuWidth p-base bg-signableBrandColours-signableBlue text-signableBrandColours-white flex items-center">
            <span>Dismiss me daddy</span>
          </div>
        </template>
      </TabGroup>
      <BaseButton
        class="mt-base"
        @click.native="resetDismissingTabGroupDemo"
      >
        Reset
      </BaseButton>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import { TabGroup } from '@/components/library/tabs/TabGroup';
import { TabItem } from '@/components/library/tabs/TabItem';
import { TabList } from '@/components/library/tabs/TabList';

const selectedListTab = ref('one');
const tabListTabs = ref([
  {
    title: 'Tab 1',
    id: 'one',
  },
  {
    title: 'Tab 2',
    id: 'two',
  },
  {
    title: 'Tab 3',
    id: 'three',
    dismissible: true,
  },
  {
    title: 'Tab 4',
    id: 'four',
    dismissible: true,
  },
  {
    title: 'Tab 5',
    id: 'five',
  },
  {
    title: 'Tab 6',
    id: 'six',
  },
]);

const selectedStupidTab = ref('0');
const stupidAmountOfTabs = ref(Array.from({ length: 60 }, (_, index) => ({
    id: index.toString(),
    title: `Tab ${index + 1}`,
  })));

const selectedGroupTab = ref('navy');
const tabGroupTabs = ref([
  {
    title: 'Navy',
    id: 'navy',
  },
  {
    title: 'Pink',
    id: 'pink',
  },
  {
    title: 'Orange',
    id: 'orange',
  },
  {
    title: 'Red',
    id: 'red',
  },
]);

const selectedDismissDemoTab = ref('1');
const tabGroupDismissDemo = () => [
  {
    title: 'Tab 1',
    id: '1',
  },
  {
    title: 'Tab 2',
    id: '2',
    dismissible: true,
  },
  {
    title: 'Tab 3',
    id: '3',
    dismissible: true,
  },
  {
    title: 'Tab 4',
    id: '4',
    dismissible: true,
  },
];

const tabGroupDismissDemoRef = ref(tabGroupDismissDemo());

/** @param {string} tabId */
function removeDismissDemoTab(tabId) {
  tabGroupDismissDemoRef.value = tabGroupDismissDemoRef.value.filter(tab => tab.id !== tabId);
}

function resetDismissingTabGroupDemo() {
  tabGroupDismissDemoRef.value = tabGroupDismissDemo();
}
</script>
